/**
 * RadioGroup — one choice from a small, fully visible set.
 *
 * Radix handles the roving tabindex and arrow-key semantics; we own the optics.
 */

import { forwardRef, useId } from 'react';
import type { ReactNode } from 'react';
import * as RRadio from '@radix-ui/react-radio-group';
import { cn } from '@/lib/cn';

export interface RadioOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

const DOT: Record<'sm' | 'md', string> = { sm: 'size-3.5', md: 'size-4' };
const PIP: Record<'sm' | 'md', string> = { sm: 'size-1.5', md: 'size-[7px]' };

export interface RadioGroupProps {
  options: RadioOption[];
  value?: string | null;
  onValueChange?: (value: string) => void;
  orientation?: 'vertical' | 'horizontal';
  size?: 'sm' | 'md';
  name?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup(
  {
    options,
    value,
    onValueChange,
    orientation = 'vertical',
    size = 'md',
    name,
    disabled = false,
    required,
    className,
    ...aria
  },
  ref,
) {
  const groupId = useId();

  return (
    <RRadio.Root
      ref={ref}
      value={value ?? undefined}
      onValueChange={onValueChange}
      disabled={disabled}
      name={name}
      required={required}
      orientation={orientation}
      className={cn(
        'flex',
        orientation === 'vertical' ? 'flex-col gap-2' : 'flex-row flex-wrap items-center gap-4',
        className,
      )}
      {...aria}
    >
      {options.map((option) => {
        const itemId = `${groupId}-${option.value}`;
        const itemDisabled = disabled || option.disabled === true;

        return (
          <div key={option.value} className="flex items-start gap-2">
            <RRadio.Item
              id={itemId}
              value={option.value}
              disabled={itemDisabled}
              className={cn(
                'grid shrink-0 place-items-center rounded-full border border-line-strong bg-surface-1',
                'transition-[background-color,border-color] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
                DOT[size],
                !itemDisabled && 'hover:border-focus',
                'data-[state=checked]:border-accent',
                'disabled:cursor-not-allowed disabled:border-line-subtle disabled:bg-surface-3',
                'disabled:data-[state=checked]:border-line-subtle',
              )}
              style={{ borderRadius: 'var(--radius-full)' }}
            >
              <RRadio.Indicator
                className={cn(
                  'block rounded-full bg-accent',
                  PIP[size],
                  itemDisabled && 'bg-muted',
                )}
              />
            </RRadio.Item>

            <label
              htmlFor={itemId}
              className={cn(
                'min-w-0 select-none',
                itemDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <span
                className={cn(
                  'flex items-center gap-1.5 text-base leading-4',
                  itemDisabled ? 'text-muted' : 'text-primary',
                  '[&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:text-muted',
                )}
              >
                {option.icon}
                {option.label}
              </span>
              {option.description ? (
                <span className="mt-1 block text-xs text-muted leading-snug">
                  {option.description}
                </span>
              ) : null}
            </label>
          </div>
        );
      })}
    </RRadio.Root>
  );
});
